import { Canvas, Ellipse, FabricObject, Line, Path, Rect } from 'fabric';
import { DrawStyle } from './drawing-tool.model';
import { DashedPencilBrush } from './dashed-pencil-brush';
import { getSelectionStylesForCreation } from './selection-styles';
import { buildArrowPath } from './arrow-geometry';

/** Default minimum shape/line size (scene units); below this a finish is treated as a stray click. */
const DEFAULT_MIN_SHAPE_SIZE = 5;

/**
 * Property a fabric object can carry to mark it transient — `FabricYjsBinding` skips
 * such objects in its canvas→Yjs handlers, so a live preview never syncs. Kept as a
 * string convention (not an import) so this lib stays free of any sync-binding dep.
 */
const EPHEMERAL_FLAG = '_yjsEphemeral';

function markEphemeral(obj: FabricObject): void {
    (obj as unknown as Record<string, unknown>)[EPHEMERAL_FLAG] = true;
}

/** Shape tools handled by {@link ShapeDrawingController.startShape}. */
export type ShapeKind = 'rectangle' | 'ellipse';

/**
 * ShapeDrawingController — framework-agnostic drag-to-draw state machine for
 * rectangle / ellipse / line on a fabric canvas. Extracted from PracticeTrainer's
 * canvas service so PT and classroom share one implementation.
 *
 * Domain-neutral: it does NOT read any store and does NOT know about Yjs. The
 * caller passes the {@link DrawStyle} (color/width/brushStyle) at the start of a
 * stroke (sourced from the app's own toolbar/state). On finish, the controller
 * makes the object selectable, applies the shared selection styles, and fires
 * `object:modified` on the canvas — so whatever sync binding the app has attached
 * (e.g. `FabricYjsBinding`, which listens to `object:added`/`object:modified`)
 * persists the final object. The caller supplies scene-space coordinates
 * (`canvas.getScenePoint(e)`), so this works regardless of canvas CSS scaling.
 *
 * The caller owns mouse-event wiring and decides which tool is active; this class
 * only manages the in-progress preview for one shape and one line at a time.
 */
export class ShapeDrawingController {
    private readonly minSize: number;

    private shapePreview: FabricObject | null = null;
    private activeShape: ShapeKind | null = null;
    private shapeStartX = 0;
    private shapeStartY = 0;

    private linePreview: Line | null = null;

    private arrowPreview: Path | null = null;
    private arrowStartX = 0;
    private arrowStartY = 0;
    private arrowStyle: DrawStyle | null = null;

    constructor(
        private readonly canvas: Canvas,
        options?: { minSize?: number }
    ) {
        this.minSize = options?.minSize ?? DEFAULT_MIN_SHAPE_SIZE;
    }

    // -------- Shape (rectangle / ellipse) --------

    /** Begin a rectangle/ellipse preview at the given scene coordinates. */
    startShape(kind: ShapeKind, sceneX: number, sceneY: number, style: DrawStyle): void {
        this.activeShape = kind;
        this.shapeStartX = sceneX;
        this.shapeStartY = sceneY;

        const strokeDashArray = DashedPencilBrush.getDashArray(style.brushStyle, style.width);

        if (kind === 'rectangle') {
            this.shapePreview = new Rect({
                left: sceneX,
                top: sceneY,
                width: 1,
                height: 1,
                fill: 'transparent',
                stroke: style.color,
                strokeWidth: style.width,
                strokeDashArray,
                selectable: false,
                evented: false,
            });
        } else {
            this.shapePreview = new Ellipse({
                left: sceneX,
                top: sceneY,
                rx: 1,
                ry: 1,
                fill: 'transparent',
                stroke: style.color,
                strokeWidth: style.width,
                strokeDashArray,
                selectable: false,
                evented: false,
            });
        }

        this.canvas.add(this.shapePreview);
        this.canvas.renderAll();
    }

    /**
     * Update the shape preview during drag. Handles negative drag directions;
     * `shiftKey` constrains to a square/circle.
     */
    updateShape(sceneX: number, sceneY: number, shiftKey: boolean): void {
        if (!this.shapePreview) return;

        let width = sceneX - this.shapeStartX;
        let height = sceneY - this.shapeStartY;

        if (shiftKey) {
            const maxDim = Math.max(Math.abs(width), Math.abs(height));
            width = width >= 0 ? maxDim : -maxDim;
            height = height >= 0 ? maxDim : -maxDim;
        }

        const left = width >= 0 ? this.shapeStartX : this.shapeStartX + width;
        const top = height >= 0 ? this.shapeStartY : this.shapeStartY + height;
        const absWidth = Math.abs(width);
        const absHeight = Math.abs(height);

        if (this.activeShape === 'rectangle') {
            this.shapePreview.set({ left, top, width: absWidth, height: absHeight });
        } else {
            // Ellipse uses rx/ry (radii), positioned from center.
            (this.shapePreview as Ellipse).set({
                left: left + absWidth / 2,
                top: top + absHeight / 2,
                rx: absWidth / 2,
                ry: absHeight / 2,
                originX: 'center',
                originY: 'center',
            });
        }

        this.shapePreview.setCoords();
        this.canvas.renderAll();
    }

    /**
     * Finish the shape: discard if too small, otherwise make it selectable, apply
     * selection styles, and fire `object:modified` so the sync binding persists it.
     * @returns true if a shape was kept, false if discarded.
     */
    finishShape(): boolean {
        if (!this.shapePreview) return false;

        let isTooSmall: boolean;
        if (this.activeShape === 'rectangle') {
            const rect = this.shapePreview as Rect;
            isTooSmall = (rect.width ?? 0) < this.minSize || (rect.height ?? 0) < this.minSize;
        } else {
            const ellipse = this.shapePreview as Ellipse;
            isTooSmall = (ellipse.rx ?? 0) < this.minSize || (ellipse.ry ?? 0) < this.minSize;
        }

        if (isTooSmall) {
            this.canvas.remove(this.shapePreview);
            this.resetShape();
            this.canvas.renderAll();
            return false;
        }

        this.shapePreview.set({
            selectable: true,
            evented: true,
            ...getSelectionStylesForCreation(),
        });

        // The object is already on the canvas (added in startShape); object:modified
        // makes the sync binding persist its final geometry/style.
        this.canvas.fire('object:modified', { target: this.shapePreview });
        this.resetShape();
        this.canvas.renderAll();
        return true;
    }

    /** Cancel an in-progress shape without keeping it. */
    cancelShape(): void {
        if (this.shapePreview) {
            this.canvas.remove(this.shapePreview);
            this.resetShape();
            this.canvas.renderAll();
        }
    }

    isDrawingShape(): boolean {
        return this.shapePreview !== null;
    }

    private resetShape(): void {
        this.shapePreview = null;
        this.activeShape = null;
    }

    // -------- Line --------

    /** Begin a straight-line preview at the given scene coordinates. */
    startLine(sceneX: number, sceneY: number, style: DrawStyle): void {
        const strokeDashArray = DashedPencilBrush.getDashArray(style.brushStyle, style.width);

        this.linePreview = new Line([sceneX, sceneY, sceneX, sceneY], {
            stroke: style.color,
            strokeWidth: style.width,
            strokeDashArray,
            strokeLineCap: 'round',
            selectable: false,
            evented: false,
        });

        this.canvas.add(this.linePreview);
        this.canvas.renderAll();
    }

    /** Update the line's end point during drag. */
    updateLine(sceneX: number, sceneY: number): void {
        if (!this.linePreview) return;
        this.linePreview.set({ x2: sceneX, y2: sceneY });
        this.linePreview.setCoords();
        this.canvas.renderAll();
    }

    /**
     * Finish the line: discard if too short, otherwise make it selectable, apply
     * selection styles, and fire `object:modified`.
     * @returns true if a line was kept, false if discarded.
     */
    finishLine(): boolean {
        if (!this.linePreview) return false;

        const x1 = this.linePreview.x1 ?? 0;
        const y1 = this.linePreview.y1 ?? 0;
        const x2 = this.linePreview.x2 ?? 0;
        const y2 = this.linePreview.y2 ?? 0;
        const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

        if (length < this.minSize) {
            this.canvas.remove(this.linePreview);
            this.linePreview = null;
            this.canvas.renderAll();
            return false;
        }

        this.linePreview.set({
            selectable: true,
            evented: true,
            ...getSelectionStylesForCreation(),
        });

        this.canvas.fire('object:modified', { target: this.linePreview });
        this.linePreview = null;
        this.canvas.renderAll();
        return true;
    }

    /** Cancel an in-progress line without keeping it. */
    cancelLine(): void {
        if (this.linePreview) {
            this.canvas.remove(this.linePreview);
            this.linePreview = null;
            this.canvas.renderAll();
        }
    }

    isDrawingLine(): boolean {
        return this.linePreview !== null;
    }

    // -------- Arrow --------
    // The preview is rebuilt each move (an arrow Path is awkward to mutate in place)
    // but marked ephemeral so the sync binding ignores the per-frame add/remove; only
    // the final arrow (added on finish) syncs — one Y.Doc write per arrow.

    private buildArrow(x1: number, y1: number, x2: number, y2: number, style: DrawStyle, ephemeral: boolean): Path {
        const arrow = new Path(buildArrowPath(x1, y1, x2, y2, style.width), {
            stroke: style.color,
            strokeWidth: style.width,
            fill: '',
            strokeLineCap: 'round',
            strokeLineJoin: 'round',
            strokeDashArray: DashedPencilBrush.getDashArray(style.brushStyle, style.width) ?? null,
            selectable: !ephemeral,
            evented: !ephemeral,
            ...(ephemeral ? {} : getSelectionStylesForCreation()),
        });
        if (ephemeral) markEphemeral(arrow);
        return arrow;
    }

    /** Begin an arrow preview at the given scene coordinates. */
    startArrow(sceneX: number, sceneY: number, style: DrawStyle): void {
        this.arrowStartX = sceneX;
        this.arrowStartY = sceneY;
        this.arrowStyle = style;
        this.arrowPreview = this.buildArrow(sceneX, sceneY, sceneX, sceneY, style, true);
        this.canvas.add(this.arrowPreview);
        this.canvas.renderAll();
    }

    /** Update the arrow's tip during drag (rebuilds the ephemeral preview). */
    updateArrow(sceneX: number, sceneY: number): void {
        if (!this.arrowPreview || !this.arrowStyle) return;
        this.canvas.remove(this.arrowPreview);
        this.arrowPreview = this.buildArrow(this.arrowStartX, this.arrowStartY, sceneX, sceneY, this.arrowStyle, true);
        this.canvas.add(this.arrowPreview);
        this.canvas.renderAll();
    }

    /**
     * Finish the arrow: discard if too short, otherwise replace the ephemeral preview
     * with a final selectable arrow whose `object:added` the sync binding persists.
     * @returns true if an arrow was kept, false if discarded.
     */
    finishArrow(sceneX: number, sceneY: number): boolean {
        if (!this.arrowPreview || !this.arrowStyle) return false;

        const length = Math.sqrt((sceneX - this.arrowStartX) ** 2 + (sceneY - this.arrowStartY) ** 2);

        // Remove the ephemeral preview either way (binding ignores this removal).
        this.canvas.remove(this.arrowPreview);

        if (length < this.minSize) {
            this.resetArrow();
            this.canvas.renderAll();
            return false;
        }

        const finalArrow = this.buildArrow(this.arrowStartX, this.arrowStartY, sceneX, sceneY, this.arrowStyle, false);
        this.canvas.add(finalArrow); // non-ephemeral → object:added → synced once
        this.resetArrow();
        this.canvas.renderAll();
        return true;
    }

    /** Cancel an in-progress arrow without keeping it. */
    cancelArrow(): void {
        if (this.arrowPreview) {
            this.canvas.remove(this.arrowPreview);
            this.resetArrow();
            this.canvas.renderAll();
        }
    }

    isDrawingArrow(): boolean {
        return this.arrowPreview !== null;
    }

    private resetArrow(): void {
        this.arrowPreview = null;
        this.arrowStyle = null;
    }
}
